import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type TouchEvent as EventoDeToque,
} from "react";
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchContentRef,
} from "react-zoom-pan-pinch";
import type { Tamanho } from "@foto/shared";
import { Sobreposicao } from "./Sobreposicao.js";
import { Botao, Chave, Girando, Segmentos, cx } from "./ui.js";
import { tamanhoDaImagem } from "./tamanho-da-imagem.js";
import {
  PASSO_DAS_SETAS_PX,
  alvoDoToqueDuplo,
  ehToqueDuplo,
  escalaDeAjuste,
  escalaDeCemPorCento,
  estaAjustada,
  legendaDaAreaUtil,
  limitesDeZoom,
  partidaAoTrocarDeVersao,
  passoDaRoda,
  proximoPasso,
  rotuloDoZoom,
  versaoInicialDoVisor,
  type AreaEmFracoes,
  type Toque,
  type VersaoDoVisor,
  type VistaAnterior,
} from "./visor.js";

/** Quanto a ação do toque duplo espera pelos eventos de mouse que o navegador imita depois do toque. */
const ATRASO_DO_TOQUE_DUPLO_MS = 50;

/** Quanto dura o zoom por botão, tecla ou toque duplo. Sem animação para quem pediu menos movimento. */
const DURACAO_DO_ZOOM_MS = 160;

/** O botão principal do visor: escolher o que se está vendo, depois de conferir. */
export interface AcaoDoVisor {
  /** "Usar esta foto", "Colocar na folha", "Usar esta capa". */
  rotulo: string;
  /** Recebe a versão que estava à vista. O visor fecha logo depois. */
  aoAcionar: (versaoId: string) => void;
}

interface PropsComuns {
  /** O nome do que se vê: vai na barra e é o que o leitor de tela anuncia ao abrir. */
  titulo: string;
  /** O que a imagem mostra, para quem não a enxerga. Com mais de uma versão, o rótulo dela se junta. */
  alt: string;
  /**
   * A área que o papel imprime, em frações da imagem. Com ela, o visor ganha a chave "Área útil do
   * papel", desligada de saída, que desenha a linha e escurece o que cai no picotado.
   */
  areaUtil?: AreaEmFracoes | undefined;
  /** O botão principal (brasa) na barra: aciona e fecha. Sem ele, o visor só mostra. */
  acao?: AcaoDoVisor | undefined;
  /**
   * O que aparece por trás da imagem. Sem ele, o fundo liso de sempre. `"xadrez"` é para as imagens com
   * transparência (figurinhas, logos, letras): sobre um fundo liso não se vê onde o recorte termina.
   */
  fundo?: "xadrez" | undefined;
  aoFechar: () => void;
}

/** Uma imagem só: a folha de marcadores, o JPEG da impressão. */
interface UmaImagem {
  /** A imagem em resolução cheia — a que o "100%" mede. */
  fotoUrl: string;
  /**
   * A versão leve, que já está na página. Aparece na hora e é trocada pela cheia quando esta carrega:
   * o zoom funciona sobre ela desde o primeiro quadro, só com menos detalhe.
   */
  previa?: string | undefined;
  /** O tamanho do arquivo em resolução cheia, em pixels: é o que "100%" representa. */
  tamanho: Tamanho;
  versoes?: undefined;
  versaoInicial?: undefined;
}

/**
 * Mais de uma imagem — ou uma só sem o tamanho conhecido, que o visor mede. Com duas ou mais, a barra
 * ganha a chave de versões: alternar mantém o zoom e a posição quando as proporções são iguais, e
 * reenquadra em "Ajustar" quando diferem.
 */
interface ComVersoes {
  versoes: readonly VersaoDoVisor[];
  /** A que abre à vista; se a lista não a tem, abre a primeira. */
  versaoInicial?: string | undefined;
  fotoUrl?: undefined;
  previa?: undefined;
  tamanho?: undefined;
}

export type VisorDeImagemProps = PropsComuns & (UmaImagem | ComVersoes);

/** Acompanha uma media query. O visor usa para o movimento reduzido e para o dedo no lugar do mouse. */
function useConsulta(consulta: string): boolean {
  const [vale, setVale] = useState(
    () => typeof matchMedia === "function" && matchMedia(consulta).matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const lista = matchMedia(consulta);
    const mudou = () => setVale(lista.matches);
    mudou();
    lista.addEventListener("change", mudou);
    return () => lista.removeEventListener("change", mudou);
  }, [consulta]);
  return vale;
}

type SituacaoDaCheia = "carregando" | "pronta" | "erro";

const SEM_TAMANHO: Tamanho = { largura: 0, altura: 0 };

/**
 * Visor de imagem em tela cheia: zoom pela roda, pela pinça e pelos botões, arrastar para mover,
 * toque duplo para alternar entre "Ajustar" e 100%.
 *
 * Os gestos são da `react-zoom-pan-pinch` (pinça, arrasto, limites, centralização). O que é nosso é
 * só o que ela não sabe: a escala em que 1 px do arquivo = 1 px da tela, os degraus dos botões, o
 * duplo toque que alterna, a linha da área útil. Esses números moram em `visor.ts`, com testes.
 *
 * O conteúdo que dá zoom é a imagem **no tamanho do arquivo**: a escala da biblioteca é, então,
 * "pixels da tela por pixel do arquivo", e 100% não precisa de conta nenhuma.
 */
export function VisorDeImagem({
  titulo,
  fotoUrl: fotoUrlDaUnica,
  previa: previaDaUnica,
  tamanho: tamanhoDaUnica,
  versoes,
  versaoInicial,
  alt,
  areaUtil,
  acao,
  fundo,
  aoFechar,
}: VisorDeImagemProps) {
  // Uma imagem só é uma lista de uma versão. Quem chama pode passar objetos novos a cada renderização;
  // o que vale são os endereços e os números, e é por eles que a lista é comparada.
  const recebidas: readonly VersaoDoVisor[] = versoes ?? [
    {
      id: "unica",
      rotulo: titulo,
      url: fotoUrlDaUnica ?? "",
      previa: previaDaUnica,
      tamanho: tamanhoDaUnica,
    },
  ];
  const assinatura = recebidas
    .map(
      (v) =>
        `${v.id}|${v.rotulo}|${v.url}|${v.previa ?? ""}|${v.tamanho?.largura ?? ""}x${v.tamanho?.altura ?? ""}`,
    )
    .join("\n");
  const lista = useMemo(() => recebidas, [assinatura]);

  // A versão à vista. Abre na pedida e muda só pela chave: se quem chama passar outra `versaoInicial`
  // depois, é de outra abertura (e de outro visor).
  const [versaoId, setVersaoId] = useState(() => versaoInicialDoVisor(lista, versaoInicial));
  const versao = lista.find((v) => v.id === versaoId) ?? lista[0]!;
  const { url: fotoUrl, previa } = versao;
  const maisDeUma = lista.length > 1;

  // O tamanho das versões que chegam sem ele, medido uma vez por endereço. As duas versões são
  // medidas já na abertura: quando a chave troca, o tamanho da outra costuma estar pronto.
  const [medidas, setMedidas] = useState<Record<string, Tamanho>>({});
  const [naoMedidas, setNaoMedidas] = useState<Record<string, true>>({});
  useEffect(() => {
    let vivo = true;
    for (const v of lista) {
      if (v.tamanho) continue;
      tamanhoDaImagem(v.url).then(
        (t) => vivo && setMedidas((m) => (m[v.url] ? m : { ...m, [v.url]: t })),
        () => vivo && setNaoMedidas((m) => ({ ...m, [v.url]: true })),
      );
    }
    return () => {
      vivo = false;
    };
  }, [lista]);

  const larguraDoArquivo = versao.tamanho?.largura ?? medidas[fotoUrl]?.largura ?? 0;
  const alturaDoArquivo = versao.tamanho?.altura ?? medidas[fotoUrl]?.altura ?? 0;
  const tamanho = useMemo<Tamanho | null>(
    () =>
      larguraDoArquivo > 0 && alturaDoArquivo > 0
        ? { largura: larguraDoArquivo, altura: alturaDoArquivo }
        : null,
    [larguraDoArquivo, alturaDoArquivo],
  );
  const naoCarregou = tamanho === null && naoMedidas[fotoUrl] === true;

  // A `Sobreposicao` já guarda o `aoFechar` numa ref, mas o "Fechar" e a ação daqui chamam a função
  // de dentro de um fecho de efeito e de um botão: a ref garante que é sempre a de agora.
  const ultimoAoFechar = useRef(aoFechar);
  ultimoAoFechar.current = aoFechar;
  const fechar = useCallback(() => ultimoAoFechar.current(), []);

  const palco = useRef<HTMLDivElement>(null);
  const controles = useRef<ReactZoomPanPinchContentRef>(null);
  const [area, setArea] = useState<Tamanho | null>(null);
  const [escalaAtual, setEscala] = useState<number | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [dica, setDica] = useState(true);
  const [mostrarArea, setMostrarArea] = useState(false);
  // A legenda da linha só fica à vista logo depois de ligá-la: sobre a imagem, ela cobriria justamente
  // a borda que se quer conferir. Quem a quiser de novo desliga e liga a chave.
  const [legenda, setLegenda] = useState(false);
  const idDaLegenda = useId();

  const reduzido = useConsulta("(prefers-reduced-motion: reduce)");
  const comDedo = useConsulta("(pointer: coarse)");

  // A imagem em resolução cheia: a leve já está na tela, e esta entra por cima quando carrega.
  const [cheia, setCheia] = useState<SituacaoDaCheia>("carregando");
  const [natural, setNatural] = useState<Tamanho | null>(null);
  useEffect(() => {
    setCheia("carregando");
    setNatural(null);
  }, [fotoUrl]);

  const aoCarregarCheia = useCallback((img: HTMLImageElement) => {
    const pronta = () => {
      setNatural({ largura: img.naturalWidth, altura: img.naturalHeight });
      setCheia("pronta");
    };
    // `decode()` espera a imagem estar pronta para pintar: sem ele, o primeiro quadro depois do
    // `load` podia sair em branco, e a leve já teria sido coberta.
    img.decode().then(pronta, () => (img.naturalWidth > 0 ? pronta() : setCheia("erro")));
  }, []);
  const aoFalharCheia = useCallback(() => setCheia("erro"), []);

  // A área do visor, medida: o ajuste depende dela, e ela muda com a rotação e o redimensionar.
  useLayoutEffect(() => {
    const el = palco.current;
    if (!el) return;
    const medir = () => {
      const largura = el.clientWidth;
      const altura = el.clientHeight;
      setArea((a) => (a && a.largura === largura && a.altura === altura ? a : { largura, altura }));
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  // Só há o que dar zoom quando a área e o tamanho da imagem são conhecidos. Até lá — uma versão que
  // ainda não foi medida, normalmente por uns instantes —, a leve aparece parada no meio do palco.
  const pronto = area !== null && area.largura > 0 && area.altura > 0 && tamanho !== null;
  const ajustada = useMemo(
    () => escalaDeAjuste(tamanho ?? SEM_TAMANHO, pronto ? area : SEM_TAMANHO),
    [tamanho, area, pronto],
  );
  const cem = natural && tamanho ? escalaDeCemPorCento(natural, tamanho) : 1;
  const limites = useMemo(() => limitesDeZoom(ajustada, cem), [ajustada, cem]);

  // De onde a versão parte ao montar: o que a anterior deixou, se a proporção é a mesma; senão, ajustada.
  // O zoom é remontado a cada troca (`key` na versão): trocar o tamanho do conteúdo com a biblioteca
  // viva faria o ajuste dela recentralizar por cima do que se quer manter.
  const [anterior, setAnterior] = useState<VistaAnterior | null>(null);
  const partida = pronto ? partidaAoTrocarDeVersao(anterior, tamanho, ajustada) : null;
  const escala = escalaAtual ?? partida?.escala ?? ajustada;
  const tempo = reduzido ? 0 : DURACAO_DO_ZOOM_MS;

  // Os manipuladores de evento vivem no `document` e no palco, registrados uma vez: leem o que mudou
  // por aqui, e não por um fecho velho.
  const vivo = useRef({ ajustada, cem, tempo });
  vivo.current = { ajustada, cem, tempo };

  // O primeiro gesto dispensa a dica e a legenda: quem já está mexendo na imagem não precisa mais delas.
  const usou = useCallback(() => {
    setDica(false);
    setLegenda(false);
  }, []);
  // E elas não ficam para sempre: sobre a imagem, viram ruído.
  useEffect(() => {
    const id = window.setTimeout(() => setDica(false), 8000);
    return () => window.clearTimeout(id);
  }, []);
  useEffect(() => {
    if (!legenda) return;
    const id = window.setTimeout(() => setLegenda(false), 10_000);
    return () => window.clearTimeout(id);
  }, [legenda]);
  const alternarArea = useCallback((ligada: boolean) => {
    setMostrarArea(ligada);
    setLegenda(ligada);
    // Quem foi mexer na chave já achou o caminho: a dica de gestos sai da frente da legenda.
    setDica(false);
  }, []);

  // Trocar de versão guarda onde a imagem estava, para a nova partir de lá quando a proporção é a mesma.
  const trocarVersao = (id: string) => {
    if (id === versao.id) return;
    const c = controles.current;
    setAnterior(
      c && tamanho
        ? {
            tamanho,
            ajustada,
            vista: { escala: c.state.scale, x: c.state.positionX, y: c.state.positionY },
          }
        : null,
    );
    // A conta de "a área mudou e a imagem estava ajustada" é de uma mesma imagem: aqui é outra.
    ultimaAjustada.current = null;
    setEscala(null);
    setVersaoId(id);
  };

  const centroDoPalco = useCallback(() => {
    const r = palco.current!.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }, []);

  const ajustar = useCallback(() => {
    void controles.current?.centerView(vivo.current.ajustada, vivo.current.tempo);
    usou();
  }, [usou]);

  const irPara = useCallback(
    (alvo: number, ponto?: { x: number; y: number }) => {
      const p = ponto ?? centroDoPalco();
      void controles.current?.zoomToPoint(alvo, p.x, p.y, vivo.current.tempo);
      usou();
    },
    [centroDoPalco, usou],
  );

  const passo = useCallback(
    (sentido: 1 | -1) => {
      const c = controles.current;
      if (!c) return;
      const { ajustada: a, cem: u } = vivo.current;
      irPara(proximoPasso(c.state.scale, sentido, a, u));
    },
    [irPara],
  );

  const alternar = useCallback(
    (x: number, y: number) => {
      const c = controles.current;
      if (!c) return;
      const { ajustada: a, cem: u } = vivo.current;
      if (alvoDoToqueDuplo(c.state.scale, a, u) === "cem") irPara(u, { x, y });
      else ajustar();
    },
    [ajustar, irPara],
  );

  // Se a área mudou (girou o celular, redimensionou a janela) e a imagem estava ajustada, ela acompanha;
  // se estava ampliada, fica como está — a biblioteca mantém dentro dos limites.
  const ultimaAjustada = useRef<number | null>(null);
  useEffect(() => {
    const c = controles.current;
    if (!c) return;
    const antes = ultimaAjustada.current;
    ultimaAjustada.current = ajustada;
    if (antes === null || antes === ajustada) return;
    if (estaAjustada(c.state.scale, antes) || c.state.scale < limites.minimo) {
      void c.centerView(ajustada, 0);
    }
  }, [ajustada, limites.minimo]);

  // Roda do mouse e pinça do trackpad. A biblioteca soma `passo × |deltaY|` à escala; aqui o `passo`
  // é refeito a cada evento — proporcional à escala e convertido de linhas para pixels — e a conta
  // da roda continua sendo dela. (`setup` é público no tipo; a versão está fixada em `package.json`.)
  // O `preventDefault` é nosso porque, sem ele, `Ctrl` + roda ampliaria a página inteira.
  useEffect(() => {
    const el = palco.current;
    if (!el) return;
    const aoRodar = (e: WheelEvent) => {
      e.preventDefault();
      const instancia = controles.current?.instance;
      if (!instancia) return;
      instancia.setup.wheel.step = passoDaRoda(instancia.state.scale, e.deltaMode, e.ctrlKey);
    };
    el.addEventListener("wheel", aoRodar, { capture: true, passive: false });
    // O Safari de mesa e o do iPhone ampliam a página por conta própria na pinça, por eventos de
    // "gesto" que `touch-action` não alcança.
    const barrar = (e: Event) => e.preventDefault();
    const gestos = ["gesturestart", "gesturechange", "gestureend"];
    gestos.forEach((g) => el.addEventListener(g, barrar));
    return () => {
      el.removeEventListener("wheel", aoRodar, { capture: true });
      gestos.forEach((g) => el.removeEventListener(g, barrar));
    };
  }, []);

  // Teclado: `+`/`=` e `-` dão um passo, `0` ajusta, `1` vai a 100%, as setas movem. Fica no `document`
  // porque o foco, ao abrir, está no painel, que não tem filhos entre ele e o `document`.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const c = controles.current;
      if (!c) return;
      const mover = (dx: number, dy: number) =>
        void c.panBy(dx, dy, Math.min(vivo.current.tempo, 100));
      switch (e.key) {
        case "+":
        case "=":
          passo(1);
          break;
        case "-":
        case "_":
          passo(-1);
          break;
        case "0":
          ajustar();
          break;
        case "1":
          irPara(vivo.current.cem);
          break;
        // As setas movem a vista, como a rolagem: "esquerda" mostra o que está à esquerda.
        case "ArrowLeft":
          mover(PASSO_DAS_SETAS_PX, 0);
          break;
        case "ArrowRight":
          mover(-PASSO_DAS_SETAS_PX, 0);
          break;
        case "ArrowUp":
          mover(0, PASSO_DAS_SETAS_PX);
          break;
        case "ArrowDown":
          mover(0, -PASSO_DAS_SETAS_PX);
          break;
        default:
          return;
      }
      e.preventDefault();
      usou();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [passo, ajustar, irPara, usou]);

  // Toque duplo. O clique duplo do mouse vem do `onDoubleClick`; no dedo, o navegador nem sempre o
  // sintetiza (o Safari do iPhone, por exemplo), então os dois toques são contados aqui. Um toque que
  // se mexeu, ou com mais de um dedo, é arrasto ou pinça — não conta.
  //
  // São eventos de **toque**, e não de ponteiro, de propósito: o `touchend` chega depois do
  // `pointerup`, e é nele que a biblioteca encerra o arrasto e, no fim, realinha a imagem aos limites.
  // Agir antes dele fazia o realinhamento desfazer o zoom que o toque duplo acabava de pedir.
  //
  // E a ação espera um instante (`ATRASO_DO_TOQUE_DUPLO_MS`): depois do `touchend`, o navegador
  // ainda dispara os eventos de mouse que imita para o toque, e o `mousedown` deles faz a biblioteca
  // interromper qualquer animação em curso — o zoom começava e era cortado no mesmo quadro.
  const toques = useRef<{
    inicio: Toque | null;
    moveu: boolean;
    ultimo: Toque | null;
    duploEm: number;
  }>({ inicio: null, moveu: false, ultimo: null, duploEm: -Infinity });

  const adiado = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(adiado.current), []);

  const aoTocar = (e: EventoDeToque) => {
    const t = toques.current;
    if (e.touches.length > 1) {
      t.moveu = true;
      return;
    }
    const dedo = e.touches[0]!;
    t.inicio = { x: dedo.clientX, y: dedo.clientY, t: performance.now() };
    t.moveu = false;
  };
  const aoMexer = (e: EventoDeToque) => {
    const t = toques.current;
    const dedo = e.touches[0];
    if (!t.inicio || !dedo) return;
    if (Math.hypot(dedo.clientX - t.inicio.x, dedo.clientY - t.inicio.y) > 10) t.moveu = true;
  };
  const aoSoltar = (e: EventoDeToque) => {
    const t = toques.current;
    // Ainda há um dedo na tela: é o fim de uma pinça, não de um toque.
    if (e.touches.length > 0) return;
    const dedo = e.changedTouches[0];
    if (t.moveu || !t.inicio || !dedo || e.type === "touchcancel") {
      t.ultimo = null;
    } else {
      const toque = { x: dedo.clientX, y: dedo.clientY, t: performance.now() };
      if (ehToqueDuplo(t.ultimo, toque)) {
        t.ultimo = null;
        t.duploEm = toque.t;
        window.clearTimeout(adiado.current);
        adiado.current = window.setTimeout(
          () => alternar(toque.x, toque.y),
          ATRASO_DO_TOQUE_DUPLO_MS,
        );
      } else {
        t.ultimo = toque;
      }
    }
    t.inicio = null;
  };
  const aoClicarDuasVezes = (e: React.MouseEvent) => {
    // O toque duplo já foi tratado acima; o navegador que também sintetiza o `dblclick` não o repete.
    if (performance.now() - toques.current.duploEm < 600) return;
    alternar(e.clientX, e.clientY);
  };

  const ajustado = estaAjustada(escala, ajustada);
  const emCem = Math.abs(escala / cem - 1) < 0.005;
  const noMaximo = escala >= limites.maximo * 0.999;
  const noMinimo = escala <= limites.minimo * 1.001;
  const ampliada = escala > ajustada * 1.005;

  // Com mais de uma versão, o leitor de tela precisa saber qual está à vista.
  const descricao = maisDeUma ? `${alt}, ${versao.rotulo}` : alt;

  return (
    <Sobreposicao rotulo={titulo} aoFechar={fechar} tamanho="tela">
      {/*
        No celular, o zoom e a ação ficam embaixo, ao alcance do polegar — quem segura o aparelho com uma
        mão só não alcança o topo. Daí a grade: o título e o "Fechar" em cima, a chave de versões logo
        abaixo, o palco no meio, os controles e a ação embaixo. A partir do `md` há largura para tudo
        numa linha só: o rodapé vira `contents`, e os controles e a ação sobem para a barra do topo.
        A ordem no código é a do Tab nessa tela larga (título, versões, controles, ação, "Fechar",
        imagem); onde a grade os põe no celular é com ela.
      */}
      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto_minmax(0,1fr)_auto] bg-fundo md:grid-cols-[minmax(0,1fr)_auto_auto_auto] md:grid-rows-[auto_auto_minmax(0,1fr)]">
        <div className="area-segura-superior col-start-1 row-start-1 min-w-0 border-b border-borda pb-2.5 pl-4 sm:pl-6">
          <h2
            title={titulo}
            className="truncate font-display text-[1.0625rem] leading-tight tracking-[-0.01em]"
          >
            {titulo}
          </h2>
          <p
            role="status"
            className={cx(
              "mt-0.5 flex items-center gap-1.5 text-[0.8125rem] leading-snug",
              cheia === "erro" || naoCarregou ? "text-descartar" : "text-texto-suave",
            )}
          >
            {cheia === "carregando" && !naoCarregou && (
              <Girando className="size-3 border-[1.5px]" />
            )}
            {naoCarregou
              ? "Não foi possível carregar a imagem."
              : cheia === "carregando"
                ? "Carregando em resolução cheia…"
                : cheia === "erro"
                  ? "Não carregou a resolução cheia: o zoom usa a versão leve."
                  : `${maisDeUma ? `${versao.rotulo}: ` : ""}${natural?.largura} × ${natural?.altura} px, resolução cheia`}
          </p>
        </div>

        {maisDeUma && (
          <div className="col-span-full row-start-2 border-b border-borda px-4 py-2 sm:px-6">
            <Segmentos
              rotulo="Versão da imagem"
              valor={versao.id}
              opcoes={lista}
              aoMudar={trocarVersao}
            />
          </div>
        )}

        <div className="area-segura-inferior col-span-full row-start-4 flex flex-col gap-2 border-t border-borda px-3 pt-2 md:contents">
          <div className="md:col-start-2 md:row-start-1 md:flex md:items-center md:border-b md:border-borda md:pr-2 md:pl-3">
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 md:flex-nowrap md:justify-end md:gap-x-2">
              <div
                role="group"
                aria-label="Zoom"
                className="inline-flex items-stretch rounded-peca border border-borda bg-superficie"
              >
                <button
                  type="button"
                  aria-label="Reduzir o zoom"
                  aria-disabled={noMinimo}
                  onClick={() => !noMinimo && passo(-1)}
                  className="grid min-h-11 min-w-11 place-items-center rounded-l-peca text-[1.25rem] leading-none text-texto transition-colors hover:bg-superficie-alta aria-disabled:cursor-not-allowed aria-disabled:text-texto-fraco aria-disabled:hover:bg-transparent"
                >
                  −
                </button>
                <span className="num grid min-w-[3.75rem] place-items-center border-x border-borda px-2 text-[0.9375rem] text-texto">
                  {rotuloDoZoom(escala, cem)}
                </span>
                <button
                  type="button"
                  aria-label="Ampliar o zoom"
                  aria-disabled={noMaximo}
                  onClick={() => !noMaximo && passo(1)}
                  className="grid min-h-11 min-w-11 place-items-center rounded-r-peca text-[1.25rem] leading-none text-texto transition-colors hover:bg-superficie-alta aria-disabled:cursor-not-allowed aria-disabled:text-texto-fraco aria-disabled:hover:bg-transparent"
                >
                  +
                </button>
              </div>

              <Botao
                tom={ajustado ? "quieto" : "fantasma"}
                aria-label="Ajustar à tela"
                aria-pressed={ajustado}
                onClick={ajustar}
                className="min-h-11 border border-borda"
              >
                Ajustar
              </Botao>
              <Botao
                tom={emCem ? "quieto" : "fantasma"}
                aria-label="Ver em 100%"
                aria-pressed={emCem}
                onClick={() => irPara(cem)}
                className="min-h-11 border border-borda"
              >
                100%
              </Botao>

              {areaUtil && (
                <div className="md:ml-2 md:border-l md:border-borda md:pl-4">
                  <Chave
                    ligada={mostrarArea}
                    aoAlternar={alternarArea}
                    rotulo="Área útil do papel"
                    texto="Área útil do papel"
                    descricaoId={idDaLegenda}
                  />
                  {/* A legenda some da tela; o leitor de tela a recebe sempre, junto da chave. */}
                  <span id={idDaLegenda} className="sr-only">
                    {legendaDaAreaUtil()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {acao && (
            // O único preenchimento sólido da tela: escolher o que se acabou de conferir.
            <div className="md:col-start-3 md:row-start-1 md:flex md:items-center md:border-b md:border-borda md:px-2">
              <Botao
                tom="brasa"
                onClick={() => {
                  acao.aoAcionar(versao.id);
                  fechar();
                }}
                className="min-h-12 w-full md:min-h-11 md:w-auto md:px-5"
              >
                {acao.rotulo}
              </Botao>
            </div>
          )}
        </div>

        <div className="area-segura-superior col-start-2 row-start-1 border-b border-borda pb-0 pr-2 sm:pr-4 md:col-start-4 md:flex md:items-center md:pt-0">
          <button
            type="button"
            onClick={fechar}
            className="inline-flex min-h-11 items-center rounded-peca px-3 text-[0.9375rem] text-texto-suave transition-colors hover:bg-superficie hover:text-texto"
          >
            Fechar
          </button>
        </div>

        <div
          ref={palco}
          onTouchStart={aoTocar}
          onTouchMove={aoMexer}
          onTouchEnd={aoSoltar}
          onTouchCancel={aoSoltar}
          onDoubleClick={aoClicarDuasVezes}
          className="relative col-span-full row-start-3 min-h-0 touch-none select-none overflow-hidden overscroll-contain bg-black"
        >
          {partida && tamanho ? (
            <TransformWrapper
              // Outra versão é outro zoom: remonta, partindo de `partida`.
              key={versao.id}
              ref={controles}
              initialScale={partida.escala}
              initialPositionX={partida.x}
              initialPositionY={partida.y}
              minScale={limites.minimo}
              maxScale={limites.maximo}
              centerOnInit={partida.centrar}
              // Imagem menor que a área fica no meio, em vez de grudada num canto.
              centerZoomedOut
              // Sem o "respiro" além dos limites: aqui o mínimo é o ajuste, e passar dele deixaria a
              // imagem menor que a tela, flutuando.
              zoomAnimation={{ size: 0 }}
              // O duplo toque é nosso: o da biblioteca alterna entre 1 e 2, e aqui a referência é o ajuste.
              doubleClick={{ disabled: true }}
              panning={{ allowRightClickPan: false, velocityDisabled: reduzido }}
              velocityAnimation={{ disabled: reduzido }}
              autoAlignment={{ animationTime: reduzido ? 0 : 200 }}
              onTransform={(_ref, { scale }) => setEscala(scale)}
              onZoomStart={usou}
              onPinchStart={usou}
              onPanningStart={(_ref, e) => {
                usou();
                // Só o arrasto do mouse troca o cursor: as setas também passam por aqui, e o toque
                // não tem cursor.
                if (e.type === "mousedown") setArrastando(true);
              }}
              onPanningStop={() => setArrastando(false)}
            >
              <TransformComponent
                wrapperStyle={{
                  width: "100%",
                  height: "100%",
                  // O navegador não pode rolar a página nem ampliá-la durante o gesto: é a biblioteca
                  // que o interpreta.
                  touchAction: "none",
                  cursor: ampliada ? (arrastando ? "grabbing" : "grab") : "default",
                }}
                contentStyle={{ width: tamanho.largura, height: tamanho.altura }}
              >
                {/* `--escala` é o que a linha da área útil usa para manter a espessura na tela. */}
                <div
                  className="size-full"
                  style={
                    {
                      "--escala": escala,
                      // O xadrez desfaz o zoom (ver `fundo-xadrez`): casas do mesmo tamanho em qualquer escala.
                      ...(fundo === "xadrez" && { "--xadrez-escala": escala }),
                    } as CSSProperties
                  }
                >
                  <ConteudoDaImagem
                    previa={previa}
                    fotoUrl={fotoUrl}
                    alt={descricao}
                    tamanho={tamanho}
                    cheiaPronta={cheia === "pronta"}
                    areaUtil={mostrarArea ? areaUtil : undefined}
                    xadrez={fundo === "xadrez"}
                    aoCarregar={aoCarregarCheia}
                    aoFalhar={aoFalharCheia}
                  />
                </div>
              </TransformComponent>
            </TransformWrapper>
          ) : (
            // Ainda sem o tamanho da versão (ou sem ela, se não carregou): a leve fica parada, sem zoom.
            <div className="absolute inset-0 grid place-items-center p-3">
              {previa && !naoCarregou && (
                <img
                  src={previa}
                  alt={descricao}
                  draggable={false}
                  className="max-h-full max-w-full object-contain opacity-70"
                />
              )}
            </div>
          )}

          {/* Avisos que só existem enquanto servem: a legenda enquanto a linha está ligada, a dica até o primeiro gesto. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-3 flex flex-col items-center gap-2 px-3">
            {areaUtil && mostrarArea && legenda && (
              <p className="flex max-w-[26rem] items-start gap-2 rounded-peca border border-borda bg-fundo/90 px-3 py-2 text-[0.8125rem] leading-relaxed text-texto-suave">
                <span
                  aria-hidden
                  className="mt-1 inline-block h-3 w-5 shrink-0 border border-dashed border-sky-400"
                />
                <span>{legendaDaAreaUtil()}</span>
              </p>
            )}
            {dica && pronto && (
              <p className="max-w-[36rem] rounded-peca bg-fundo/85 px-3 py-1.5 text-center text-[0.8125rem] leading-snug text-texto-suave">
                {comDedo
                  ? "Use dois dedos para ampliar · toque duas vezes para 100%"
                  : "Role para ampliar · arraste para mover · clique duas vezes para ver em 100%"}
              </p>
            )}
          </div>
        </div>
      </div>
    </Sobreposicao>
  );
}

/**
 * A imagem — a leve por baixo, a cheia por cima — e a linha da área útil. Não muda com o zoom (a
 * transformação é do contêiner), então fica fora do ciclo de renderização dos gestos.
 */
const ConteudoDaImagem = memo(function ConteudoDaImagem({
  previa,
  fotoUrl,
  alt,
  tamanho,
  cheiaPronta,
  areaUtil,
  xadrez,
  aoCarregar,
  aoFalhar,
}: {
  previa: string | undefined;
  fotoUrl: string;
  alt: string;
  tamanho: Tamanho;
  cheiaPronta: boolean;
  areaUtil: AreaEmFracoes | undefined;
  xadrez: boolean;
  aoCarregar: (img: HTMLImageElement) => void;
  aoFalhar: () => void;
}) {
  const cheia = useRef<HTMLImageElement>(null);

  // Imagem que já estava no cache pode ter carregado antes de o `onLoad` existir.
  useEffect(() => {
    const el = cheia.current;
    if (el?.complete && el.naturalWidth > 0) aoCarregar(el);
  }, [fotoUrl, aoCarregar]);

  return (
    // Uma só imagem para quem não enxerga: as duas camadas são a mesma folha.
    <div
      role="img"
      aria-label={alt}
      className={cx("relative size-full", xadrez ? "fundo-xadrez" : "bg-superficie")}
    >
      {previa && (
        <img
          src={previa}
          alt=""
          draggable={false}
          className="pointer-events-none absolute inset-0 size-full"
        />
      )}
      <img
        ref={cheia}
        key={fotoUrl}
        src={fotoUrl}
        alt=""
        draggable={false}
        decoding="async"
        onLoad={(e) => aoCarregar(e.currentTarget)}
        onError={aoFalhar}
        style={{ opacity: cheiaPronta ? 1 : 0 }}
        className="pointer-events-none absolute inset-0 size-full"
      />
      {areaUtil && <AreaUtilSobreAImagem area={areaUtil} tamanho={tamanho} />}
    </div>
  );
});

/**
 * A linha tracejada azul da área útil e o escurecido do que fica fora dela — o mesmo desenho do
 * editor de enquadramento. Fica dentro do conteúdo, então acompanha a imagem no zoom e no arrasto.
 *
 * O SVG tem as medidas do arquivo (viewBox em pixels dele), e como o contêiner é escalado pelo zoom, a
 * espessura e o tracejado dividem pela escala (`--escala`) para parecerem constantes na tela: 1,5 px a
 * 33% e a 400%. Sem isso a linha engrossaria junto com a imagem.
 */
function AreaUtilSobreAImagem({ area, tamanho }: { area: AreaEmFracoes; tamanho: Tamanho }) {
  const { largura: L, altura: A } = tamanho;
  const x = area.x * L;
  const y = area.y * A;
  const w = area.largura * L;
  const h = area.altura * A;
  return (
    <svg
      aria-hidden
      data-area-util
      viewBox={`0 0 ${L} ${A}`}
      className="pointer-events-none absolute inset-0 size-full text-sky-400"
    >
      {/* O retângulo da imagem inteira com o da área recortado (evenodd): sobra só o que está fora. */}
      <path
        d={`M0 0H${L}V${A}H0Z M${x} ${y}h${w}v${h}h${-w}Z`}
        fillRule="evenodd"
        fill="rgb(0 0 0 / 0.34)"
      />
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke="currentColor"
        style={{
          strokeWidth: "calc(1.5px / var(--escala))",
          strokeDasharray: "calc(6px / var(--escala)) calc(4px / var(--escala))",
        }}
      />
    </svg>
  );
}
